import type {TripLookup, TripUnitAmenity, TripUnitPmsData, TripUnitPmsHousingRules, TripUnitPmsPropertyManagement} from "@/entities/trip/api/types";

import {DateHelper} from "@/shared/lib";

export interface TripBedroomInfo {
    id: string | number;
    name: string;
    sleeps: number;
    beds: Array<{type: string; count: number}>;
}

function extractBedType(title: string): string {
    return title
        .replace(/^\d+\s+/, "")
        .replace(/\s*beds?$/i, "")
        .trim();
}

const FALLBACK_SUPPORT_CONTACT: TripUnitPmsPropertyManagement = {
    name: "Casago",
    email: "guestservices@casago.com",
    phone: null,
};

class TripDetailModelClass {
    constructor(private readonly trip: TripLookup) {}

    // ==================== Guest / reservation ====================

    get id(): string {
        return this.trip.reservation.id;
    }

    get guestFullName(): string {
        const {firstName, lastName} = this.trip.guest;
        return `${firstName} ${lastName}`.trim();
    }

    get confirmationCode(): string {
        return this.trip.reservation.confirmationCode ?? "";
    }

    get guestCounts(): {adults: number; children: number; pets: number} {
        const {adults, children, pets} = this.trip.guest;
        return {adults, children, pets};
    }

    // ==================== Pricing ====================

    get total(): number {
        return this.trip.finances.total;
    }

    get balance(): number | null {
        return this.trip.finances.balance;
    }

    get paid(): number | null {
        return this.trip.finances.paid;
    }

    get rent(): number {
        return this.trip.finances.rent;
    }

    get fees(): number {
        return this.trip.finances.fees;
    }

    get taxes(): number {
        return this.trip.finances.taxes;
    }

    get currencyCode(): string {
        return this.trip.finances.bookedCurrencyCode;
    }

    get isPaidInFull(): boolean {
        return this.balance != null && this.balance <= 0;
    }

    // ==================== Cancellation / rental agreement ====================

    get cancellationPolicyName(): string {
        return this.trip.reservation.cancellationPolicy ?? "";
    }

    /** Title-cased policy name, e.g. "MODERATE" -> "Moderate" */
    get cancellationPolicyLabel(): string {
        const name = this.cancellationPolicyName;
        if (!name) return "";
        return name.charAt(0) + name.slice(1).toLowerCase();
    }

    get cancellationPolicyText(): string {
        return this.trip.reservation.cancellationPolicyText ?? "";
    }

    get rentalAgreementCompleted(): boolean {
        return this.trip.reservation.rentalAgreement.completed;
    }

    get rentalAgreementCompletedAt(): string | null {
        return this.trip.reservation.rentalAgreement.completedAt;
    }

    get rentalAgreementText(): string {
        return this.trip.reservation.rentalAgreement.agreementText ?? "";
    }

    // ==================== Access / getting there ====================

    get addressLines(): {street: string; cityStateZip: string} {
        const {address1, address2, city, state, zip} = this.trip.unit.address;
        return {
            street: [address1, address2].filter(Boolean).join(", "),
            cityStateZip: [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
        };
    }

    get latitude(): number | null {
        return this.trip.unit.address.latitude;
    }

    get longitude(): number | null {
        return this.trip.unit.address.longitude;
    }

    get drivingDirections(): string | null {
        return this.trip.access.drivingDirections;
    }

    get accessNotes(): string | null {
        return this.trip.access.accessNotes;
    }

    get importantInfo(): string | null {
        return this.trip.unit.importantInfo;
    }

    // ==================== Stay ====================

    get checkInDate(): string {
        return this.trip.stay.checkInDate;
    }

    get checkOutDate(): string {
        return this.trip.stay.checkOutDate;
    }

    get nights(): number {
        return DateHelper.calculateNights(this.checkInDate, this.checkOutDate);
    }

    private get pmsData(): TripUnitPmsData {
        return (this.trip.unit.pmsData as TripUnitPmsData | null) ?? {};
    }

    get checkInTime(): string {
        const time = this.pmsData.checkInTime;
        return time ? DateHelper.formatTime24to12(time) : "";
    }

    get checkOutTime(): string {
        const time = this.pmsData.checkOutTime;
        return time ? DateHelper.formatTime24to12(time) : "";
    }

    // ==================== Unit ====================

    get unitName(): string {
        return this.trip.unit.name;
    }

    get city(): string {
        return this.trip.unit.address.city;
    }

    get state(): string {
        return this.trip.unit.address.state;
    }

    get locationLabel(): string {
        const {city, state, countryCode} = this.trip.unit.address;
        return [city, state, countryCode].filter(Boolean).join(", ").toUpperCase();
    }

    get mainImage(): {src: string; alt: string} {
        const photo = [...this.trip.unit.photos].sort((a, b) => a.imageRank - b.imageRank)[0];
        return {
            src: photo?.url ?? "",
            alt: this.unitName,
        };
    }

    get maxOccupancy(): number {
        return this.trip.unit.maxOccupancy;
    }

    get fullBaths(): number {
        return this.trip.unit.fullBaths;
    }

    get halfBaths(): number {
        return this.trip.unit.halfBaths;
    }

    get bedsSummaryText(): string {
        const summary = this.trip.homeInfo.bedsSummary;
        if (!summary || summary.length === 0) return "";
        return summary.map((item) => `${item.value} ${item.displayName}`).join(" · ");
    }

    get bedrooms(): Array<TripBedroomInfo> {
        const details = this.trip.homeInfo.bedroomsDetails;
        if (!Array.isArray(details)) return [];

        return details.flatMap((entry, index) => {
            if (typeof entry !== "object" || entry === null) return [];
            const bedroom = entry as Record<string, unknown>;
            const beds = Array.isArray(bedroom.beds) ? (bedroom.beds as Array<Record<string, unknown>>) : [];

            return [
                {
                    id: typeof bedroom.number === "number" ? bedroom.number : index,
                    name: typeof bedroom.title === "string" ? bedroom.title : "",
                    sleeps: typeof bedroom.sleep === "number" ? bedroom.sleep : 0,
                    beds: beds.flatMap((bed) => {
                        const title = typeof bed.title === "string" ? bed.title : "";
                        const quantity = typeof bed.quantity === "number" ? bed.quantity : 0;
                        if (!title) return [];
                        return [{type: extractBedType(title), count: quantity}];
                    }),
                },
            ];
        });
    }

    // ==================== Amenities ====================

    get amenities(): Array<TripUnitAmenity> {
        return this.trip.unit.amenities;
    }

    // ==================== House rules ====================

    get houseRulesHtml(): string {
        return this.trip.homeInfo.houseRules ?? "";
    }

    get housingRules(): TripUnitPmsHousingRules | null {
        return this.pmsData.housingRules ?? null;
    }

    // ==================== Property management / support ====================

    get propertyManagement(): TripUnitPmsPropertyManagement {
        return this.pmsData.propertyManagement ?? FALLBACK_SUPPORT_CONTACT;
    }

    get propertyManagementName(): string {
        return this.propertyManagement.name;
    }

    get propertyManagementEmail(): string {
        return this.propertyManagement.email ?? FALLBACK_SUPPORT_CONTACT.email!;
    }

    get propertyManagementPhone(): string | null {
        return this.propertyManagement.phone;
    }
}

export type TripDetailModelInstance = TripDetailModelClass;

export function TripDetailModel(trip: TripLookup): TripDetailModelInstance {
    return new TripDetailModelClass(trip);
}
